'use client';

import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { lazy, Suspense } from 'react';
import { useForm } from 'react-hook-form';
import type { InsuranceProvider } from '@//app/api/commerce/insurance/provider/[id]';
import type { ElasticVehicle } from '@//app/api/search/vehicles/[id]';
import { Alert } from '@//components/Alert';
import { Button } from '@//components/Button';
import { Disclaimer } from '@//components/Disclaimer';
import { InputSelection } from '@//components/InputSelection';
import { Logo } from '@//components/Logo';
import { Spinner } from '@//components/Spinner';
import { Selectfield } from '@//containers/Selectfield';
import { Textfield } from '@//containers/Textfield';
import useIfInsuranceBySsn from '@//data/ecom/useIfInsuranceBySsn';
import { useLegacyTrackInsuranceInterested } from '@//data/tracking/useLegacyTrackInsuranceInterested';
import { useMarket } from '@//providers/MarketSettingsProvider';
import { OdometerReadingUnit, odometerReadingUnitByMarket } from '@//utils/odometer';
import { WidgetHeading } from '../../../WidgetHeading';
import styles from '../InsuranceAdvanced.module.scss';
import { getDefaultValues, IfInsuranceSchema } from './utils';

const IfInsuranceModal = lazy(() => import('./IfInsuranceModal'));

const Options = {
  [OdometerReadingUnit.ScandinavianMile]: [
    { value: '1', label: '0–1000 mil' },
    { value: '2', label: '1000–1500 mil' },
    { value: '3', label: '1500–2000 mil' },
    { value: '4', label: '2 500+ mil' },
  ],
  [OdometerReadingUnit.Kilometer]: [
    { value: '1', label: '0–10000 km' },
    { value: '2', label: '10000–15000 km' },
    { value: '3', label: '15000–20000 km' },
    { value: '4', label: '25 000+ km' },
  ],
};

const IfTrackingValues = {
  [OdometerReadingUnit.ScandinavianMile]: {
    '1': '0-1000',
    '2': '1000-1500',
    '3': '1500-2000',
    '4': '2000+',
  },
  [OdometerReadingUnit.Kilometer]: {
    '1': '0-10000',
    '2': '10000-15000',
    '3': '15000-20000',
    '4': '20000+',
  },
};

interface IfInsuranceProps {
  branchId: string;
  itemId: string;
  insuranceProvider: InsuranceProvider;
  vehicle: ElasticVehicle;
}

const IfInsurance: React.FC<IfInsuranceProps> = ({
  branchId,
  itemId,
  insuranceProvider,
  vehicle,
}) => {
  const tValidation = useTranslations('utils.validation');
  const t = useTranslations('pages.item.informationSection');
  const market = useMarket();
  const mutation = useIfInsuranceBySsn();
  const { trackLegacyInsuranceInterested } = useLegacyTrackInsuranceInterested();

  const {
    formState: { errors },
    handleSubmit,
    register,
  } = useForm({
    resolver: zodResolver(IfInsuranceSchema(tValidation)),
    defaultValues: getDefaultValues(),
  });

  const onSubmit = handleSubmit((data) => {
    const options = IfTrackingValues[odometerReadingUnitByMarket[market]];
    trackLegacyInsuranceInterested({
      vehicle,
      information: {
        drivingRange: options[data.drivingDistance as keyof typeof options],
        identifier: insuranceProvider.identifier,
        institute: insuranceProvider.institute,
      },
    });
    mutation.mutate({
      drivingDistance: data.drivingDistance,
      socialId: data.socialId,
      branchId,
      vehicleId: itemId,
    });
  });

  const onCloseModal = () => mutation.reset();

  return (
    <>
      {mutation.data && (
        <Suspense fallback={null}>
          <IfInsuranceModal
            insuranceProvider={insuranceProvider}
            insurances={mutation.data.response.insurances}
            onClose={onCloseModal}
          />
        </Suspense>
      )}
      {insuranceProvider.logo && (
        <Logo
          src={insuranceProvider.logo}
          alt={insuranceProvider.title}
          width={120}
          height={48}
          logoSize="md"
        />
      )}
      <WidgetHeading id="insurance-heading">Räkna på försäkring</WidgetHeading>
      <form
        aria-labelledby="insurance-heading"
        aria-describedby="insurance-disclaimer"
        className={styles.form}
      >
        <div className={styles.formGrid}>
          <Textfield
            id="insurance-personal-identity-number"
            label="Personnummer"
            placeholder="ÅÅMMDD-XXXX"
            state={errors.socialId ? 'error' : undefined}
            stateMessage={errors.socialId?.message}
            {...register('socialId')}
          />
          <Selectfield
            id="insurance-yearly-mileage"
            label="Uppskattad körsträcka per år"
            placeholder="Välj uppskattad körsträcka per år"
            options={Options[odometerReadingUnitByMarket[market]]}
            {...register('drivingDistance')}
          />
        </div>
        <InputSelection
          type="checkbox"
          id="insurance-save-personal-identity-number"
          label="Spara personnummer på denna enhet"
          description="Spara personnummer på denna dator för att direkt se försäkringskostnader på Wayke. Uppgifterna lagras endast i din webbläsare, inte hos Wayke."
        />
      </form>
      {mutation.isError && (
        <Alert id="alert-insurance-error" variant="critical" role="alert">
          Ett fel uppstod när försäkringar skulle hämtas. Försök igen senare.
        </Alert>
      )}
      <footer className={styles.footer}>
        <Button
          variant="primary"
          size="md"
          fullWidth
          onClick={onSubmit}
          disabled={mutation.isPending}
        >
          Visa försäkringar {mutation.isPending && <Spinner size="text" />}
        </Button>
        <Disclaimer id="insurance-disclaimer">{t('insurance.disclaimer')}</Disclaimer>
      </footer>
    </>
  );
};

export default IfInsurance;
